<!-- The panel element itself is what goes fullscreen, so the browser shows this and nothing else. -->
<div class="card mb-0" #panel>
  <div class="card-body">

    <!-- Header: when it was last read, how long until the next read, and the legend -->
    <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-1">
      <h4 class="header-title mb-0">{{ 'dashboard.tv.title' | translate }}</h4>

      <div class="d-flex flex-wrap align-items-center gap-2">
        <small class="text-muted text-nowrap">
          @if (updatedAt) {
            {{ 'dashboard.tv.updatedAt' | translate:{ time: (updatedAt | date: 'HH:mm:ss') } }}
          } @else {
            {{ 'dashboard.tv.waitingFirstRead' | translate }}
          }
        </small>

        <!-- Cadence: the same choice control the campaign screens use for their polling. -->
        <button type="button"
          class="btn btn-sm trend-button btn-primary fw-bold w-auto text-nowrap mdi mdi-refresh dropdown-toggle"
          [matMenuTriggerFor]="cadenceMenu"
          [matTooltip]="'dashboard.tv.refreshIntervalLabel' | translate">
          {{ 'dashboard.tv.cadence' | translate:{ minutes: refreshMinutes } }}
        </button>
        <mat-menu #cadenceMenu="matMenu">
          @for (minutes of refreshOptions; track minutes) {
            <button mat-menu-item type="button" (click)="chooseRefreshMinutes(minutes)">
              {{ 'dashboard.tv.cadence' | translate:{ minutes: minutes } }}
            </button>
          }
        </mat-menu>

        <button type="button" class="btn btn-sm btn-soft-primary w-auto text-nowrap" [disabled]="reading" (click)="refreshNow()">
          <i class="mdi mdi-refresh me-1"></i>{{ 'dashboard.tv.refreshNow' | translate }}
        </button>

        <button type="button" class="btn btn-sm btn-primary w-auto text-nowrap" (click)="toggleFullscreen()">
          <i class="mdi me-1" [ngClass]="isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen'"></i>
          {{ (isFullscreen ? 'dashboard.tv.exitFullscreen' : 'dashboard.tv.fullscreen') | translate }}
        </button>

        <small class="text-muted text-nowrap">
          <i class="mdi mdi-circle text-danger me-1"></i>{{ 'dashboard.tv.legendAct' | translate }}
          <i class="mdi mdi-circle text-warning ms-2 me-1"></i>{{ 'dashboard.tv.legendAttention' | translate }}
          <i class="mdi mdi-circle text-success ms-2 me-1"></i>{{ 'dashboard.tv.legendOk' | translate }}
        </small>
      </div>
    </div>

    <!-- How far into the five minutes the panel is -->
    <div class="progress mb-3" role="progressbar" [attr.aria-label]="'dashboard.tv.cadence' | translate:{ minutes: refreshSeconds / 60 }">
      <div class="progress-bar bg-primary" [style.width.%]="refreshProgress"></div>
    </div>

    @if (!live || !overview) {
      <p class="text-muted mb-0">{{ 'dashboard.tv.waitingFirstRead' | translate }}</p>
    } @else {

      <!-- Agora: o que exige ação -->
      <h5 class="mb-2">{{ 'dashboard.dashboard.nowSectionTitle' | translate }}</h5>
      <div class="row g-2 mb-3">
        <div class="col-6 col-xl">
          <div class="card h-100 mb-0">
            <div class="card-body p-2">
              <div class="d-flex justify-content-between align-items-start gap-1">
                <small class="fw-bold text-uppercase">{{ 'dashboard.dashboard.nowQueueTitle' | translate }}</small>
                <span class="badge campaign-badge flex-shrink-0" [ngClass]="statusBadgeClass(queueStatus())"></span>
              </div>
              <div class="d-flex align-items-baseline gap-2">
                <h2 class="mb-0 text-nowrap" [ngClass]="statusTextClass(queueStatus())">{{ formatNumber(live.queueNow) }}</h2>
                @if (liveDelta('queueNow') !== null) {
                  <small [ngClass]="deltaClass(liveDelta('queueNow'), false)">
                    <i class="mdi" [ngClass]="deltaIcon(liveDelta('queueNow'))"></i>{{ formatDelta(liveDelta('queueNow')) }}
                  </small>
                }
              </div>
              <small class="text-muted">{{ 'dashboard.dashboard.nowQueueSubtitle' | translate }}</small>
            </div>
          </div>
        </div>
        <div class="col-6 col-xl">
          <div class="card h-100 mb-0">
            <div class="card-body p-2">
              <div class="d-flex justify-content-between align-items-start gap-1">
                <small class="fw-bold text-uppercase">{{ 'dashboard.dashboard.nowDelayedTitle' | translate }}</small>
                <span class="badge campaign-badge flex-shrink-0" [ngClass]="statusBadgeClass(countStatus(live.delayed, 1, 20))"></span>
              </div>
              <div class="d-flex align-items-baseline gap-2">
                <h2 class="mb-0 text-nowrap" [ngClass]="statusTextClass(countStatus(live.delayed, 1, 20))">{{ formatNumber(live.delayed) }}</h2>
                @if (liveDelta('delayed') !== null) {
                  <small [ngClass]="deltaClass(liveDelta('delayed'), false)">
                    <i class="mdi" [ngClass]="deltaIcon(liveDelta('delayed'))"></i>{{ formatDelta(liveDelta('delayed')) }}
                  </small>
                }
              </div>
              <small class="text-muted">{{ 'dashboard.dashboard.nowDelayedSubtitle' | translate:{ minutes: live.staleAfterMinutes } }}</small>
            </div>
          </div>
        </div>
        <div class="col-6 col-xl">
          <div class="card h-100 mb-0">
            <div class="card-body p-2">
              <div class="d-flex justify-content-between align-items-start gap-1">
                <small class="fw-bold text-uppercase">{{ 'dashboard.dashboard.nowOutOfHoursTitle' | translate }}</small>
                <span class="badge campaign-badge flex-shrink-0" [ngClass]="statusBadgeClass(countStatus(live.outOfHoursPendingCount, 1, 5))"></span>
              </div>
              <div class="d-flex align-items-baseline gap-2">
                <h2 class="mb-0 text-nowrap" [ngClass]="statusTextClass(countStatus(live.outOfHoursPendingCount, 1, 5))">{{ formatMinutes(live.outOfHoursPendingMaxMinutes) }}</h2>
                @if (liveDelta('outOfHoursPendingMaxMinutes') !== null) {
                  <small [ngClass]="deltaClass(liveDelta('outOfHoursPendingMaxMinutes'), false)">
                    <i class="mdi" [ngClass]="deltaIcon(liveDelta('outOfHoursPendingMaxMinutes'))"></i>{{ formatDeltaMinutes(liveDelta('outOfHoursPendingMaxMinutes')) }}
                  </small>
                }
              </div>
              <small class="text-muted">{{ 'dashboard.dashboard.nowOutOfHoursSubtitle' | translate:{ count: live.outOfHoursPendingCount } }}</small>
            </div>
          </div>
        </div>
        <div class="col-6 col-xl">
          <div class="card h-100 mb-0">
            <div class="card-body p-2">
              <small class="fw-bold text-uppercase d-block">{{ 'dashboard.dashboard.nowStaleTitle' | translate }}</small>
              <div class="d-flex align-items-baseline gap-2">
                <h2 class="mb-0 text-nowrap" [ngClass]="statusTextClass(countStatus(live.staleChats, 1, 10))">{{ formatNumber(live.staleChats) }}</h2>
                @if (liveDelta('staleChats') !== null) {
                  <small [ngClass]="deltaClass(liveDelta('staleChats'), false)">
                    <i class="mdi" [ngClass]="deltaIcon(liveDelta('staleChats'))"></i>{{ formatDelta(liveDelta('staleChats')) }}
                  </small>
                }
              </div>
              <small class="text-muted">{{ 'dashboard.dashboard.nowStaleSubtitle' | translate:{ minutes: live.staleAfterMinutes } }}</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Hoje contra a meta -->
      <h5 class="mb-2">{{ 'dashboard.dashboard.goalSectionTitle' | translate }}</h5>
      <div class="row g-2 mb-3">
        <div class="col-6 col-xl-2">
          <div class="card h-100 mb-0">
            <div class="card-body p-2">
              <small class="fw-bold text-uppercase d-block">{{ 'dashboard.dashboard.goalSlaTitle' | translate }}</small>
              <h2 class="mb-0 text-nowrap" [ngClass]="statusTextClass(overview.sla.status)">{{ formatPercent(overview.sla.value) }}</h2>
              <small class="text-muted">{{ 'dashboard.dashboard.goalSlaTarget' | translate:{ target: overview.sla.target, minutes: overview.goals.firstResponseMinutes } }}</small>
            </div>
          </div>
        </div>
        <div class="col-6 col-xl-2">
          <div class="card h-100 mb-0">
            <div class="card-body p-2">
              <small class="fw-bold text-uppercase d-block">{{ 'dashboard.dashboard.goalTmeTitle' | translate }}</small>
              <h2 class="mb-0 text-nowrap" [ngClass]="statusTextClass(overview.tme.status)">{{ formatMinutes(overview.tme.p90) }}</h2>
              <small class="text-muted">{{ 'dashboard.dashboard.goalTmeSubtitle' | translate }}</small>
            </div>
          </div>
        </div>
        <div class="col-6 col-xl-2">
          <div class="card h-100 mb-0">
            <div class="card-body p-2">
              <small class="fw-bold text-uppercase d-block">{{ 'dashboard.dashboard.goalTmaTitle' | translate }}</small>
              <h2 class="mb-0 text-nowrap" [ngClass]="statusTextClass(overview.tma.status)">{{ formatMinutes(overview.tma.p90) }}</h2>
              <small class="text-muted">{{ 'dashboard.dashboard.goalTmaSubtitle' | translate }}</small>
            </div>
          </div>
        </div>
        <div class="col-6 col-xl-2">
          <div class="card h-100 mb-0">
            <div class="card-body p-2">
              <small class="fw-bold text-uppercase d-block">{{ 'dashboard.dashboard.goalTmfTitle' | translate }}</small>
              <h2 class="mb-0 text-nowrap" [ngClass]="statusTextClass(overview.tmf.status)">{{ formatMinutes(overview.tmf.p90) }}</h2>
              <small class="text-muted">{{ 'dashboard.dashboard.goalTmfSubtitle' | translate }}</small>
            </div>
          </div>
        </div>
        <div class="col-6 col-xl-2">
          <div class="card h-100 mb-0">
            <div class="card-body p-2">
              <small class="fw-bold text-uppercase d-block">{{ 'dashboard.dashboard.goalAbandonmentTitle' | translate }}</small>
              <h2 class="mb-0 text-nowrap" [ngClass]="statusTextClass(overview.abandonment.status)">{{ formatPercent(overview.abandonment.value) }}</h2>
              <small class="text-muted">{{ 'dashboard.dashboard.goalAbandonmentTarget' | translate:{ target: overview.abandonment.target } }}</small>
            </div>
          </div>
        </div>
        <div class="col-6 col-xl-2">
          <div class="card h-100 mb-0">
            <div class="card-body p-2">
              <small class="fw-bold text-uppercase d-block">{{ 'dashboard.dashboard.goalVolumeTitle' | translate }}</small>
              <h2 class="mb-0 text-nowrap" [ngClass]="statusTextClass(overview.volume.status)">{{ formatSigned(overview.volume.variation) }}</h2>
              <small class="text-muted">{{ 'dashboard.dashboard.goalVolumeSubtitle' | translate }}</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Money and the month -->
      <div class="row g-2">
        <div class="col-12 col-xl-4">
          <div class="card h-100 mb-0">
            <div class="card-body">
              <h5 class="header-title mb-2">{{ 'dashboard.dashboard.moneyTitle' | translate }}</h5>
              <div class="d-flex justify-content-between align-items-center border-bottom pb-2 mb-2">
                <small class="fw-bold">{{ 'dashboard.dashboard.moneyAtRisk' | translate }}</small>
                <h4 class="mb-0 text-primary">{{ overview.money.revenueAtRisk | currency:'BRL':'symbol':'1.0-0' }}</h4>
              </div>
              <div class="d-flex justify-content-between align-items-center border-bottom pb-2 mb-2">
                <small class="fw-bold">{{ 'dashboard.dashboard.moneyLost' | translate }}</small>
                <h4 class="mb-0 text-primary">{{ overview.money.lostToDelay | currency:'BRL':'symbol':'1.0-0' }}</h4>
              </div>
              <div class="d-flex justify-content-between align-items-center border-bottom pb-2 mb-2">
                <small class="fw-bold">{{ 'dashboard.dashboard.moneyCrmWon' | translate }}</small>
                <h4 class="mb-0 text-success">{{ overview.money.crm.wonValue | currency:'BRL':'symbol':'1.0-0' }}</h4>
              </div>
              <div class="d-flex justify-content-between align-items-center">
                <small class="fw-bold">{{ 'dashboard.dashboard.moneyCrmOpen' | translate }}</small>
                <h4 class="mb-0 text-primary">{{ overview.money.crm.openValue | currency:'BRL':'symbol':'1.0-0' }}</h4>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-xl-4">
          <div class="card h-100 mb-0">
            <div class="card-body">
              <h5 class="header-title mb-2">{{ 'dashboard.tv.monthlyGoalTitle' | translate }}</h5>
              @if (salesTarget > 0) {
                <h1 class="mb-1 text-primary">{{ formatPercent(salesProgress, 1) }}</h1>
                <div class="progress mb-2" role="progressbar">
                  <div class="progress-bar bg-success" [style.width.%]="salesProgress"></div>
                </div>
                <p class="text-muted mb-3">
                  {{ 'dashboard.tv.monthlyGoalDetail' | translate:{
                    won: (salesSoFar | currency:'BRL':'symbol':'1.0-0'),
                    target: (salesTarget | currency:'BRL':'symbol':'1.0-0'),
                    remaining: (salesRemaining | currency:'BRL':'symbol':'1.0-0'),
                    perDay: (perWorkingDay | currency:'BRL':'symbol':'1.0-0'),
                    days: workingDaysLeft
                  } }}
                </p>
              } @else {
                <p class="text-muted mb-3">{{ 'dashboard.tv.monthlyGoalNotSet' | translate }}</p>
              }

              <p class="text-uppercase text-muted small mb-2">{{ 'dashboard.tv.openFunnelTitle' | translate }}</p>
              @for (stage of funnelStages; track stage.name) {
                <div class="mb-2">
                  <div class="d-flex justify-content-between align-items-baseline gap-2">
                    <small class="fw-bold text-truncate">{{ stage.name }}</small>
                    <small class="text-muted text-nowrap">{{ stage.deals }} · {{ stage.value | currency:'BRL':'symbol':'1.0-0' }}</small>
                  </div>
                  <div class="progress" role="progressbar" [attr.aria-label]="stage.name">
                    <div class="progress-bar bg-info" [style.width.%]="stage.share"></div>
                  </div>
                </div>
              } @empty {
                <p class="text-muted mb-0">{{ 'dashboard.crm.noDataForPeriod' | translate }}</p>
              }
            </div>
          </div>
        </div>

        <div class="col-12 col-xl-4">
          <div class="card h-100 mb-0">
            <div class="card-body">
              <h5 class="header-title mb-2">{{ 'dashboard.tv.topSellersTitle' | translate }}</h5>
              @for (seller of topSellers; track seller.name) {
                <div class="d-flex justify-content-between align-items-center border-bottom py-1">
                  <div class="d-flex flex-column">
                    <span class="fw-bold text-truncate">{{ seller.name }}</span>
                    <small class="text-muted">{{ 'dashboard.tv.sellerDeals' | translate:{ count: seller.deals } }}</small>
                  </div>
                  <h5 class="mb-0 text-success text-nowrap">{{ seller.value | currency:'BRL':'symbol':'1.0-0' }}</h5>
                </div>
              } @empty {
                <p class="text-muted mb-0">{{ 'dashboard.crm.noDataForPeriod' | translate }}</p>
              }

              <p class="text-uppercase text-muted small mb-2 mt-3">{{ 'dashboard.tv.topProductsTitle' | translate }}</p>
              @for (product of topProducts; track product.name) {
                <div class="d-flex justify-content-between align-items-center border-bottom py-1">
                  <div class="d-flex flex-column">
                    <span class="fw-bold text-truncate">{{ product.name }}</span>
                    <small class="text-muted">{{ 'dashboard.tv.productSales' | translate:{ count: product.sales } }}</small>
                  </div>
                  <h5 class="mb-0 text-primary text-nowrap">{{ product.value | currency:'BRL':'symbol':'1.0-0' }}</h5>
                </div>
              } @empty {
                <p class="text-muted mb-0">{{ 'dashboard.crm.noDataForPeriod' | translate }}</p>
              }
            </div>
          </div>
        </div>
      </div>

      <div class="alert alert-info mt-3 mb-0 py-2" role="alert">
        <p class="mb-0"><i class="mdi mdi-information-outline me-1"></i>{{ 'dashboard.dashboard.p90Explanation' | translate }}</p>
      </div>
    }
  </div>
</div>
